import {
  BausteinAuswahlStatus,
  SpartenType,
  TaasTarifrisikoschluesselTKV_V2,
  TaasTkvV2Data,
  Tierart,
   TierKrankheit } from '@ps20/bff-models';
import {
  convertTkvV2TaasBausteinToTarifrisikoschluessel,
  TierartEnum, ValueLabelElement
} from '@ps20-frontend/frontend-models';
import {
  buildBasicSalesProductsForShop,
  buildBasicSalesProductsForStrecke,
  parseDatum,
} from '@ps20-frontend/preis-lib';
import { convertDateFormatFromLocalStateToShortIso } from '@ps20-frontend/utils';
import { DogSize, Versicherungsnehmer } from '../../reducers/versicherungsnehmer.reducer';
import { IAppState } from '../../appState';
import { CoverageType } from '../../models/vertragsoptionen.model';
import { selectShouldDisplayBadgeInclusive } from '../../selectors/produkt.selector';

export function buildTaasTkvV2DataForStrecke(state: IAppState): TaasTkvV2Data {
  const tkvData: TaasTkvV2Data = generateBasicTaasTkvV2Data(state);
  tkvData.salesProducts = buildBasicSalesProductsForStrecke(
    state.produkt,
    convertTkvV2TaasBausteinToTarifrisikoschluessel,
  );

  if (selectShouldDisplayBadgeInclusive(state)) {
    tkvData.salesProducts = removeTwBaustein(tkvData.salesProducts);
  }

  return tkvData;
}

export function buildTaasTkvV2DataForShop(state: IAppState): TaasTkvV2Data {
  const tkvData: TaasTkvV2Data = generateBasicTaasTkvV2Data(state);
  tkvData.salesProducts = buildBasicSalesProductsForShop(
    state.produkt,
    convertTkvV2TaasBausteinToTarifrisikoschluessel,
  );

  if (selectShouldDisplayBadgeInclusive(state)) {
    tkvData.salesProducts = removeTwBaustein(tkvData.salesProducts);
  }


  return tkvData;
}

function removeTwBaustein(salesProducts: {
  [index: string]: BausteinAuswahlStatus<TaasTarifrisikoschluesselTKV_V2>[];
}): {
  [index: string]: BausteinAuswahlStatus<TaasTarifrisikoschluesselTKV_V2>[];
} {
  if (!salesProducts) {
    return salesProducts;
  }

  const filteredSalesProducts: {
    [index: string]: BausteinAuswahlStatus<TaasTarifrisikoschluesselTKV_V2>[];
  } = {};

  Object.keys(salesProducts).forEach(key => {
    filteredSalesProducts[key] = salesProducts[key].filter(
      (baustein: BausteinAuswahlStatus<TaasTarifrisikoschluesselTKV_V2>) =>
        baustein.schluessel !== TaasTarifrisikoschluesselTKV_V2.TW,
    );
  });

  return filteredSalesProducts;
}

function generateBasicTaasTkvV2Data(state: IAppState): TaasTkvV2Data {
  const gesundheitsfragen: Array<TierKrankheit> =
    (state.versicherungsnehmer.useExistingWechselbaustein === false || state.vertragsoptionen.hasWechselbaustein === false) &&  state.versicherungsnehmer.hasIllnesses === false ? [] :
    state.versicherungsnehmer.selectedIllnesses?.map(illness => (TierKrankheit as Record<string, TierKrankheit>)[illness.code]);
  return {
    hasHeilbehandlungen: state.versicherungsnehmer.hasIllnesses,
    isKastriert: false,
    spartenType: SpartenType.TIERKRANKEN_V2,
    anzVorschaedenOp: state.versicherungsnehmer.selectedIllnesses
      ? state.versicherungsnehmer.selectedIllnesses.length
      : 0,
    geburtsdatumTier: convertDateFormatFromLocalStateToShortIso(
      state.versicherungsnehmer.animalBirthDate,
    ),
    kennungVorvers: false,
    tierart: convertAnimalTypeToTierart(state.versicherungsnehmer.animalType),
    tiergeschlecht: state.versicherungsnehmer.animalGender,
    tierrasse: getBreedValueOrLabel(state.versicherungsnehmer, 'value'),
    plzVN: state.risikoort.plz,
    salesProducts: null,
    tierhaltung: state.versicherungsnehmer.catKeepingType,
    geburtsdatumVN: parseDatum(state.versicherungsnehmer.geburtsdatum),
    sublimit: 0,
    fullCoverage: state.vertragsoptionen.coverageType === CoverageType.VOLLSCHUTZ,
    urbeginndatum: state.vertragsoptionen.urbeginn,
    internerVertragsbezugs: state.taasInfo?.internerVertragsbezugs,
    gesundheitsfragen,
  };
}
export function getBreedObject(versicherungsnehmer: Versicherungsnehmer): ValueLabelElement {
  const { breed, dogSize, dogMotherBreed } = versicherungsnehmer;
  return breed ??
    dogMotherBreed ??
    (dogSize ? DOG_SIZE_CODE_MAP[dogSize] : null);
}

export function getBreedValueOrLabel(versicherungsnehmer: Versicherungsnehmer, valueOrLabel: 'value' | 'label'): string | undefined {
  const { breed, dogSize, dogMotherBreed } = versicherungsnehmer;

  return (
    (breed?.[valueOrLabel]) ??
    (DOG_SIZE_CODE_MAP[dogSize]?.[valueOrLabel]) ??
    (dogMotherBreed?.[valueOrLabel])
  );
}
export const DOG_SIZE_CODE_MAP: Partial<Record<DogSize, { value: string; label: string }>> = {
  [DogSize.LARGE]: { value: 'H132', label: 'Großer Hund' },
  [DogSize.SMALL]: { value: 'H133', label: 'Kleiner Hund' },
};

export function convertAnimalTypeToTierart(tierart: TierartEnum): Tierart {
  switch (tierart) {
    case TierartEnum.HUND:
      return Tierart.VRHUND;
    case TierartEnum.KATZE:
      return Tierart.VRKATZE;
    case TierartEnum.PFERD:
      return Tierart.VRPFERD;
    default:
      return null;
  }
}
